import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Send, Facebook, Instagram, Linkedin } from "lucide-react";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { insertContactSchema } from "@shared/schema";
import emailjs from "@emailjs/browser";
import { analyticsEvents, trackEvent } from "@/lib/ga4";

export default function ContactSection() {
  const { toast } = useToast();

  const contactSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    phone: z.string().min(1, "Phone number is required"),
    serviceInterest: z.string().min(1, "Please select a service"),
    message: z.string().min(1, "Message is required"),
  });

  type ContactFormData = z.infer<typeof contactSchema>;

  const emailConfig = {
    serviceId: "service_4pz4dzf",
    templateId: "template_aelz1vr",
    publicKey: "uhHa5ey8ZNE5hSWsN",
    toEmails: [
      "it@travelwingsonline.in",
      "support@travelwingsonline.in",
      "hr@travelwingsonline.in",
    ],
  };

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      serviceInterest: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      if (
        !emailConfig.serviceId ||
        !emailConfig.templateId ||
        !emailConfig.publicKey
      ) {
        throw new Error("Email service is not configured");
      }

      const messageBody = [
        "Hello Team,",
        "",
        "We have received a new message through the website contact form. Below are the details submitted by the customer:",
        "",
        `Name: ${data.firstName} ${data.lastName}`,
        `Email: ${data.email}`,
        `Phone: ${data.phone}`,
        `Service Interest: ${data.serviceInterest}`,
        "",
        "Message:",
        data.message,
        "",
        "Source: Website Contact Form",
      ].join("\n");

      const emailParams = {
        first_name: data.firstName,
        last_name: data.lastName,
        service_interest: data.serviceInterest,
        reply_to: data.email,
        user_email: data.email,
        phone: data.phone,
        message: messageBody,
        to_email: emailConfig.toEmails.join(","),
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        emailParams,
        { publicKey: emailConfig.publicKey }
      );

      return { status: "email_sent" };
    },
    onSuccess: () => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: "contact_form",
          item_name: "Contact Form",
          page_type: "contact",
          result: "success",
        },
      });
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours.",
      });
      form.reset();
    },
    onError: (error: Error) => {
      trackEvent({
        name: analyticsEvents.generateLead,
        params: {
          content_type: "lead",
          item_id: "contact_form",
          item_name: "Contact Form",
          page_type: "contact",
          result: "error",
          message: error.message,
        },
      });
      toast({
        title: "Failed to send message",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    contactMutation.mutate(data);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Registered Office",
      subtitle: "Main location",
      content:
        "105, 1st Floor, Pocket-B Market, Sarita Vihar, New Delhi-110076",
      color: "blue",
    },
    {
      icon: MapPin,
      title: "Branch Office",
      subtitle: "Additional locations",
      content: [
        "2-P&2-F, 2nd Floor, DCM Building, 16 Barakhamba Road, New Delhi-110001",
        "C-28, Shop#5, Masoodpur, Vasant Kunj, New Delhi-110070",
      ],
      color: "purple",
    },
    {
      icon: Phone,
      title: "Call Us",
      subtitle: "Mon-Sat 9AM-6PM",
      content: [
        "011-41616942, 011-41716942, 011-42860239",
        "011-49983453",
        "011-46048710",
        "+91 88826 40963",
        "+91 98108 52274",
        "Vasant Kunj: +91 9582700184",
        "+91 95827 00912",
      ],
      color: "green",
    },
    {
      icon: Mail,
      title: "Email Us",
      subtitle: "Quick response guaranteed",
      content: "support@travelwingsonline.in",
      color: "yellow",
    },
  ];

  const getIconColor = (color: string) => {
    switch (color) {
      case "blue":
        return "text-blue-600";
      case "purple":
        return "text-purple-600";
      case "green":
        return "text-green-600";
      case "yellow":
        return "text-yellow-600";
      default:
        return "text-gray-600";
    }
  };

  const getIconBg = (color: string) => {
    switch (color) {
      case "blue":
        return "bg-blue-100";
      case "purple":
        return "bg-purple-100";
      case "green":
        return "bg-green-100";
      case "yellow":
        return "bg-yellow-100";
      default:
        return "bg-gray-100";
    }
  };

  return (
    <section id="contact" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Ready to start your journey? Contact us for personalized travel and
            forex solutions
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-6">
                Send us a Message
              </h3>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your first name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter your last name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="Enter your email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder="Enter your phone number"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="serviceInterest"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service Interest</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="forex">
                              Currency Exchange
                            </SelectItem>
                            <SelectItem value="packages">
                              Holiday Packages
                            </SelectItem>
                            <SelectItem value="cards">Forex Cards</SelectItem>
                            <SelectItem value="insurance">
                              Travel Insurance
                            </SelectItem>
                            <SelectItem value="visa">Visa Services</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us about your requirements"
                            rows={5}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={contactMutation.isPending}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {contactMutation.isPending ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>

          <div className="space-y-8">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex items-center mb-4">
                    <div
                      className={`w-12 h-12 ${getIconBg(
                        info.color
                      )} rounded-lg flex items-center justify-center mr-4`}
                    >
                      <IconComponent
                        className={`w-6 h-6 ${getIconColor(info.color)}`}
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">
                        {info.title}
                      </h4>
                      <p className="text-gray-600 text-sm">{info.subtitle}</p>
                    </div>
                  </div>
                  {Array.isArray(info.content) ? (
                    <div className="space-y-1">
                      {info.content.map((item, itemIndex) => (
                        <p key={itemIndex} className="text-gray-700">
                          {item}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-700">{info.content}</p>
                  )}
                </div>
              );
            })}

            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <h4 className="font-semibold text-gray-800 mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/Travelwingsforex/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/travelwingsforex1/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/travel-wings-forex-private-limted/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 hover:bg-blue-200 transition-colors"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Google Maps Section */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Find Our Main Office
            </h3>
            <p className="text-gray-600">
              Visit us at our registered office in Sarita Vihar, New Delhi
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3505.2441434558646!2d77.2854745!3d28.5272264!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce3d8cf00f8b5%3A0x3be5fa7b9b8c8d8e!2sSarita%20Vihar%2C%20New%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1635420000000!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen={false}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
              title="Travel Wings Forex Office Location"
            ></iframe>
          </div>
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              For job related queries, visit our{" "}
              <a
                href="/careers"
                className="text-blue-600 hover:underline font-medium"
              >
                careers page
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
